import React, { useState } from 'react';
import {
  Check,
  X,
  Shield,
  Zap,
  Lock,
  ArrowRight,
  Server,
  Info,
} from 'lucide-react';

import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Group,
  List,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import useConfig from "./configProvider";

function PricingCard({
  icon,
  iconColor,
  name,
  description,
  price,
  period,
  cta,
  ctaVariant,
  ctaColor,
  featuresTitle,
  features,
  popular = false,
  popularLabel,
  ctaHref,
  ctaDisabled,
  betaLabel,
  note,
}) {
  return (
    <Paper withBorder radius="xl" p="xl" h="100%">
      <Stack gap="lg" h="100%">
        <Group justify="space-between" align="flex-start">
          <ThemeIcon variant="light" color={iconColor} size={48} radius="md">
            {icon}
          </ThemeIcon>
          {popular && (
            <Badge variant="filled" color="teal" radius="xl">
              {popularLabel}
            </Badge>
          )}
        </Group>

        <Stack gap={4}>
          <Title order={3}>{name}</Title>
          <Text c="dimmed" fz="sm">
            {description}
          </Text>
        </Stack>

        <Group align="flex-end" gap={4}>
          <Text fz="xl" fw={700}>
            $
          </Text>
          <Text fz={42} fw={800} lh={1}>
            {price}
          </Text>
          <Text c="dimmed" pb={8}>
            {period}
          </Text>
        </Group>

        <Stack gap="sm" mt="xs">
          <Text fw={700} fz="sm">
            {featuresTitle}
          </Text>
          <List spacing="sm">
            {features.map((item) => (
              <List.Item
                key={item.label}
                icon={
                  item.included ? (
                    <ThemeIcon size={18} radius="xl" variant="light" color="teal">
                      <Check size={12} />
                    </ThemeIcon>
                  ) : (
                    <ThemeIcon size={18} radius="xl" variant="light" color="gray">
                      <X size={12} />
                    </ThemeIcon>
                  )
                }
              >
                <Group gap={8} wrap="nowrap" align="center">
                  <Text c={item.included ? undefined : 'dimmed'}>{item.label}</Text>
                  {item.beta && betaLabel && (
                    <Badge size="xs" variant="light" color="teal" radius="xl">
                      {betaLabel}
                    </Badge>
                  )}
                </Group>
              </List.Item>
            ))}
          </List>
        </Stack>

        {note && (
          <Text fz="xs" c="dimmed" fs="italic">
            {note}
          </Text>
        )}

        <Button
          component={ctaHref ? 'a' : undefined}
          href={ctaHref}
          target={ctaHref ? '_blank' : undefined}
          variant={ctaVariant}
          color={ctaColor}
          disabled={ctaDisabled}
          size="md"
          rightSection={<ArrowRight size={16} />}
          fullWidth
          mt="auto"
        >
          {cta}
        </Button>
      </Stack>
    </Paper>
  );
}

export default function PricingOptions() {
  const { t } = useTranslation();
  const [annual, setAnnual] = useState(true);
  const colorScheme = useComputedColorScheme('light');
  const config = useConfig();
  const installUrl = config?.data?.githubAppSlug
    ? `https://github.com/apps/${config.data.githubAppSlug}/installations/new`
    : null;

  return (
    <Box mih="100vh" py={80}>
      <Container size="xl">
        <Stack gap="xl">
          <Stack align="center" gap="sm">
            <Title order={1} ta="center">
              {t('Simple, Transparent Pricing')}
            </Title>
            <Text c="dimmed" ta="center" maw={760}>
              {t("Mathematical proof of your intellectual property doesn't have to be expensive. Protect your codebase for less than a cup of coffee.")}
            </Text>

            <Group gap="sm" mt="xs">
              <SegmentedControl
                value={annual ? 'annual' : 'monthly'}
                onChange={(value) => setAnnual(value === 'annual')}
                data={[
                  { label: t('Monthly'), value: 'monthly' },
                  { label: t('Annually'), value: 'annual' },
                ]}
              />
              <Badge variant="light" color="teal" radius="xl">
                {t('Save 20%')}
              </Badge>
            </Group>
          </Stack>

          <Alert
            variant="light"
            color="teal"
            radius="lg"
            icon={<Info size={20} />}
            title={t('Pro beta: all Pro features are free until 31 December 2026')}
          >
            {t('We are beta-testing the Pro Agency plan, so the Free plan currently includes every Pro feature at no cost. When the beta ends on 31 December 2026, the Free plan returns to its standard feature set and the Pro limits are enforced again.')}
          </Alert>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
            <PricingCard
              icon={<Zap size={24} />}
              iconColor="blue"
              name={t('Free')}
              description={t('For open-source projects and public repositories.')}
              price="0"
              period={t('/forever')}
              cta={t('Start for Free')}
              ctaHref={installUrl}
              ctaVariant="outline"
              ctaColor="gray"
              featuresTitle={t("What's included")}
              betaLabel={t('Beta')}
              features={[
                { label: t('Unlimited Public Repositories'), included: true },
                { label: t('Daily Bitcoin Batching'), included: true },
                { label: t('Status Badge'), included: true },
                { label: t('Unlimited Private Repositories'), included: true, beta: true },
                { label: t('Private Shadow Repos'), included: true, beta: true },
                { label: t('Multiple Agency Clients'), included: true, beta: true },
                { label: t('Downloadable PDF Audit Reports'), included: true, beta: true },
                { label: t('Zero-Knowledge GitHub Action'), included: false },
              ]}
            />

            <PricingCard
              icon={<Shield size={24} />}
              iconColor="teal"
              name={t('Pro Agency')}
              description={t('For dev shops and startups building proprietary IP.')}
              price={annual ? '49' : '59'}
              period={t('/month')}
              cta={t('Get Started')}
              ctaVariant="filled"
              ctaColor="teal"
              ctaDisabled
              popular
              popularLabel={t('Coming Soon')}
              featuresTitle={t('Everything in Free, plus:')}
              features={[
                { label: t('Unlimited Private Repositories'), included: true },
                { label: t('Private Shadow Repos'), included: true },
                { label: t('Multiple Agency Clients'), included: true },
                { label: t('Downloadable PDF Audit Reports'), included: true },
                { label: t('Zero-Knowledge GitHub Action'), included: false },
              ]}
                note={t('Included free during the Pro beta (until 31 Dec 2026).')}
            />

            <PricingCard
              icon={<Lock size={24} />}
              iconColor="grape"
              name={t('Enterprise ZK')}
              description={t('Maximum security and zero codebase access for paranoid teams.')}
              price={annual ? '199' : '249'}
              period={t('/month')}
              cta={t('Contact Sales')}
              ctaVariant="outline"
              ctaColor="gray"
              ctaDisabled
              popular
              popularLabel={t('Coming Soon')}
              featuresTitle={t('Everything in Pro, plus:')}
              features={[
                { label: t('Unlimited Repositories'), included: true },
                { label: t('Zero-Knowledge Pipeline'), included: true },
                { label: t('Dedicated Account Manager'), included: true },
              ]}
            />
          </SimpleGrid>

          <Paper
            withBorder
            radius="xl"
            p="xl"
            bg={colorScheme === 'dark' ? 'dark.7' : 'gray.0'}
          >
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
              <Stack gap="sm">
                <Group gap="md" wrap="nowrap">
                  <ThemeIcon variant="light" color="blue" size={56} radius="md">
                    <Server size={28} />
                  </ThemeIcon>
                  <Title order={2}>
                    {t('Self-Hosted / Air-Gapped')}
                  </Title>
                </Group>
                <Text c="dimmed">
                  {t('Deploy the entire Timestamp GIT engine behind your own firewall using our official Docker image. Connects to public GitHub or GitHub Enterprise Server. Perfect for heavily regulated industries.')}
                </Text>
              </Stack>

              <Stack gap="md" justify="center">
                <List spacing="sm">
                  <List.Item
                    icon={
                      <ThemeIcon size={18} radius="xl" variant="light" color="teal">
                        <Check size={12} />
                      </ThemeIcon>
                    }
                  >
                    {t('100% internal network execution')}
                  </List.Item>
                  <List.Item
                    icon={
                      <ThemeIcon size={18} radius="xl" variant="light" color="teal">
                        <Check size={12} />
                      </ThemeIcon>
                    }
                  >
                    {t('No external API calls to our servers')}
                  </List.Item>
                  <List.Item
                    icon={
                      <ThemeIcon size={18} radius="xl" variant="light" color="teal">
                        <Check size={12} />
                      </ThemeIcon>
                    }
                  >
                    {t('Connects to GitHub or GitHub Enterprise Server')}
                  </List.Item>
                </List>

                <Button
                  component="a"
                  href="/docker-license"
                  variant="outline"
                  color="gray"
                  size="md"
                  rightSection={<ArrowRight size={16} />}
                >
                  {t('Request a Demo License')}
                </Button>
                <Text c="dimmed" fz="sm" ta="center">
                  {t('The requested Docker license is a time-limited demo license for evaluation purposes.')}
                </Text>
              </Stack>
            </SimpleGrid>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}